// javascript-obfuscator:disable
"use client";

import { useEffect, useState } from "react";
import lvConstants from "./liveChatConstants";

interface SalesforceChatProps {
  language?: string;
  provinceCode?: string;
}

declare global {
  interface Window {
    embedded_svc: any;
    sforce: any;
    __sfdcSiteUrlPrefix?: string;
  }
}

const env = process.env.NEXT_PUBLIC_APP_ENV || "Dev";

const LiveChatConstants = lvConstants[env as keyof typeof lvConstants];

const SalesforceChat: React.FC<SalesforceChatProps> = ({
  language = "en",
  provinceCode = "",
}) => {
  const [isClient, setIsClient] = useState(false);

  // Only run on client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Exit early if not on client side
    if (!isClient || typeof window === "undefined") return;

    let isInitialized = false;

    // Add custom styles
    const addStyles = () => {
      if (document.getElementById("salesforce-chat-styles")) return;

      const style = document.createElement("style");
      style.id = "salesforce-chat-styles";
      style.textContent = `
        .embeddedServiceHelpButton .helpButton .uiButton {
          font-family: "Arial", sans-serif;
          background-color: #48a548!important;
          border-radius: 50%!important;
          min-width: 5em;
          margin: 0 15px 25px 0!important;
        }
        .embeddedServiceHelpButton .helpButton .uiButton:focus {
          outline: 1px solid #48a548!important;
          border-radius: 50%;
          min-width: 5em;
        }
        .embeddedServiceHelpButton .helpButton {
          margin: 0 15px 25px 0;
        }
        .embeddedServiceHelpButton .helpButton .uiButton .embeddedServiceIcon {
          margin: auto;
          display: inline-block !important;
        }
        .embeddedServiceHelpButton .embeddedServiceIcon::before {
          font-size: 3em!important;
        }
        .embeddedServiceHelpButton .helpButton .uiButton .helpButtonLabel {
          display: none;
        }
        .dockableContainer img {
          width: 36px !important;
        }
        h2[embeddedService-chatHeader_chatHeader] {
          color: rgba(0, 0, 0, 0);
        }
        .embeddedServiceLiveAgentStateChatMenuMessage .rich-menu-item,
        .embeddedServiceLiveAgentStateChatMenuMessage .rich-menu-itemOptionIsClicked {
          font-size: 13px;
          padding: 5px 5px;
        }
        .embeddedServiceLiveAgentStateChatMessage .uiOutputRichText {
          font-size: 13px;
        }
        .embeddedServiceSidebarForm .embeddedServiceSidebarFormField {
          margin-top: 10px;
        }
        .remove {
          margin-top: 10px !important;
        }
        .embeddedServiceSidebarMinimizedDefaultUI .minimizedText {
          display: none !important;
        }
        .embeddedServiceSidebarMinimizedDefaultUI .embeddedServiceIcon .icon {
          font-size: 2em;
          display: inline-block !important;
        }
        span.help.x-small.embeddedServiceIcon {
          margin-right: 0px;
        }
        .embeddedServiceSidebarMinimizedDefaultUI.helpButton {
          border-radius: 50%;
          min-width: 4em !important;
          margin: 0 15px 25px 0;
          width: 4em !important;
        }
        .embeddedServiceSidebarMinimizedDefaultUI .content {
          background-color: #48a548;
          border-radius: 50%;
        }
        .embeddedServiceSidebarMinimizedDefaultUI {
          border-radius: 50% !important;
          min-width: 4.4em !important;
          background: transparent !important;
          margin: 0 15px 25px 0 !important;
          width: 4.4em !important;
          right: 27px !important;
        }
        .embeddedServiceSidebarForm .remove{
          padding: 10px !important;
        }
        .embeddedServiceSidebarForm .remove a{
          color: #000000 !important;
        }
        .modalContainer .minimizedContainer .content .messageContent .loadingBallContainer{
          display:none !important;
        }
        .embeddedServiceSidebarForm .remove { 
          padding: 10px; 
        } 
        .embeddedServiceSidebarForm .remove a { 
          text-decoration: underline; 
        } 
        .sidebarMinimized .sidebarHeader.minimizedContainer.embeddedServiceSidebarMinimizedDefaultUI{
          border: none;
        }
        .embeddedServiceSidebarMinimizedDefaultUI .messageContent:before  {
          font-family: "embeddedserviceiconfont";
          font-size: 42px;
          content: attr(data-icon);
          speak: none;
          text-rendering: auto;
          font-weight: normal;
          font-variant: normal;
          text-transform: none;
          -webkit-font-smoothing: antialiased;
        }
        .sidebarMinimized .sidebarHeader.minimizedContainer.embeddedServiceSidebarMinimizedDefaultUI {
          border: none;
          text-decoration: none;
        }
        .embeddedServiceSidebarMinimizedDefaultUI .minimizedImage .uiImage{
          display: none;
        }
        .sidebarHeader .content .embeddedServiceIcon {
          display: none;
        }
        button.sidebarHeader .minimizedImage{
          margin:0;
        } 
        sidebarHeader button.minimizeButton:after{
          bottom: 10px;
          content: "";
        }
        button.minimizeButton:hover[lwc-56vbshdpqil]:before, button.minimizeButton:hover:before {
          background: transparent;
        }
        .helpButton span.closechat {
          font-size: 18px;
          display: inline-block;
          justify-content: center;
          align-items: center;
          padding: 2px;
          position: absolute;
          border: 0;
          color: white;
          background-color: #006241;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          right: 0px;
          cursor: pointer;
          bottom: 35px;
          line-height: 1;
          text-align: center;
        }
        sidebarHeader button.minimizeButton:after{
          bottom: 10px;
        }
      `;
      document.head.appendChild(style);
    };

    // Add the click event script
    const addClickEventScript = () => {
      if (document.getElementById("salesforce-click-script")) return;

      const script = document.createElement("script");
      script.id = "salesforce-click-script";
      script.type = "text/javascript";
      script.textContent = `
        document.addEventListener('click', function (e) {
          if (e.target.closest('.helpButton')) {
            e.preventDefault();
            var newChatIcon = setInterval(function () {
              var isViewIcon = document.querySelector('.messageContent');
              var isVisible = isViewIcon && isViewIcon.offsetParent !== null;
              if (isVisible) {
                var targetElement = document.querySelector('.modalContainer .minimizedContainer .content .messageContent');
                if (targetElement) {
                  targetElement.setAttribute("data-icon", "");

                }
                clearInterval(newChatIcon);
              }
            }, 1000);
          }
        });
      `;
      document.head.appendChild(script);
    };

    // Add the close button functionality script
    const addCloseButtonScript = () => {
      if (document.getElementById("salesforce-close-button-script")) return;

      const script = document.createElement("script");
      script.id = "salesforce-close-button-script";
      script.type = "text/javascript";
      script.textContent = `
        (function () {
          function addCloseButton() {
            if (!document.querySelector('.closechat')) {
              var helpButton = document.querySelector('.helpButton');
              if (helpButton) {
                var closeSpan = document.createElement('span');
                closeSpan.className = 'closechat';
                closeSpan.innerHTML = '&times;';
                helpButton.appendChild(closeSpan);

                closeSpan.addEventListener('click', function () {
                  var embeddedHelp = document.querySelector('.embeddedServiceHelpButton');
                  if (embeddedHelp) {
                    embeddedHelp.remove();
                  }
                });
              }
            }
          }

          // Run immediately if elements already exist
          addCloseButton();

          // Use MutationObserver to watch for dynamically injected .helpButton
          var observer = new MutationObserver(function () {
            addCloseButton();
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        })();
      `;
      document.head.appendChild(script);
    };

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.defer = true;
        script.async = false; // Ensure scripts load in order

        script.onload = () => {
          console.log(`Script loaded: ${src}`);
          resolve();
        };

        script.onerror = (error) => {
          console.error(`Failed to load script: ${src}`, error);
          reject(new Error(`Failed to load ${src}`));
        };

        document.head.appendChild(script);
      });
    };

    const initializeChat = async () => {
      if (isInitialized) return;

      try {
        console.log("Starting Salesforce chat initialization...");

        // Add styles, click event script, and close button script
        addStyles();
        addClickEventScript();
        addCloseButtonScript();

        // Set global variables
        if (LiveChatConstants.SFDC_SITE_URL_PREFIX) window.__sfdcSiteUrlPrefix = LiveChatConstants.SFDC_SITE_URL_PREFIX;

        // Load Salesforce API scripts sequentially
        if (LiveChatConstants.SFSC_AJAX_CONNECTION) await loadScript(LiveChatConstants.SFDC_SITE_URL_PREFIX);

        if (LiveChatConstants.SFSC_AJAX_APEX) await loadScript(LiveChatConstants.SFSC_AJAX_APEX);

        console.log("Salesforce API scripts loaded successfully");

        const initESW = (gslbBaseURL: string | null) => {
          if (!window.embedded_svc) {
            console.error("Embedded service not available");
            return;
          }

          console.log("Configuring embedded service...");

          const embedded_svc = window.embedded_svc;

          embedded_svc.settings.displayHelpButton = true;
          embedded_svc.settings.language = language;
          embedded_svc.settings.defaultMinimizedText = " ";
          embedded_svc.settings.prepopulatedPrechatFields = {
            SCC_Province__c: provinceCode,
          };
          embedded_svc.settings.enabledFeatures = ["LiveAgent"];
          embedded_svc.settings.entryFeature = "LiveAgent";
          embedded_svc.settings.avatarImgURL = "/images/chat-avatar.png";
          embedded_svc.settings.widgetHeight = "550px";

          embedded_svc.settings.extraPrechatInfo = [
            {
              entityName: "Contact",
              saveToTranscript: "Contact",
              showOnCreate: false,
              linkToEntityField: "ContactId",
              entityFieldMaps: [
                {
                  doCreate: false,
                  doFind: false,
                  fieldName: "LastName",
                  isExactMatch: true,
                  label: "Last Name",
                },
                {
                  doCreate: false,
                  doFind: false,
                  fieldName: "FirstName",
                  isExactMatch: true,
                  label: "First Name",
                },
                {
                  doCreate: false,
                  doFind: false,
                  fieldName: "Email",
                  isExactMatch: true,
                  label: "Email",
                },
              ],
            },
            {
              entityName: "Account",
              linkToEntityField: "AccountId",
              saveToTranscript: "AccountId",
              showOnCreate: true,
              entityFieldMaps: [
                {
                  doCreate: false,
                  doFind: true,
                  fieldName: "PersonEmail",
                  isExactMatch: true,
                  label: "Email",
                },
              ],
            },
            {
              entityName: "Case",
              entityFieldMaps: [
                {
                  isExactMatch: false,
                  fieldName: "SCC_Phone__c",
                  doCreate: false,
                  doFind: false,
                  label: "Phone",
                },
                {
                  isExactMatch: false,
                  fieldName: "SCC_EMAIL__c",
                  doCreate: false,
                  doFind: false,
                  label: "Email",
                },
                {
                  isExactMatch: false,
                  fieldName: "SCC_Province__c",
                  doCreate: false,
                  doFind: false,
                  label: "Province",
                },
              ],
            },
          ];

          embedded_svc.settings.extraPrechatFormDetails = [
            {
              label: "First Name",
              transcriptFields: ["SCC_First_Name__c"],
            },
            {
              label: "Last Name",
              transcriptFields: ["SCC_Last_Name__c"],
            },
            {
              label: "Email",
              transcriptFields: ["SCC_EMAIL__c"],
            },
            {
              label: "Phone",
              transcriptFields: ["SCC_Phone__c"],
            },
            {
              label: "Province",
              transcriptFields: ["SCC_Province__c"],
            },
            {
              label: "Banner",
              value: "Sobeys",
              transcriptFields: ["SCC_Banner__c"],
            },
            {
              label: "send transcript",
              transcriptFields: ["send_transcript__c"],
            },
          ];

          // Helper function to create transcript button
          const createTranscriptButton = (data: any) => {
            setTimeout(() => {
              const node = document.querySelector(".endChatButton");
              if (!node || document.getElementById("steButton")) return;

              const btn = document.createElement("BUTTON");
              btn.id = "steButton";
              btn.innerHTML =
                '<span class="label bBody">Send Transcript to Email</span>';
              btn.setAttribute("aria-label", "Send Transcript to Email");
              btn.className =
                "endChatButton saveTranscriptButton uiButton--inverse uiButton embeddedServiceSidebarButton";

              btn.addEventListener("click", () => {
                try {
                  if (!window.sforce) {
                    console.error("Salesforce API not available");
                    return;
                  }

                  const chatSessionKey = new window.sforce.Xml(
                    "LiveAgentSessionKey"
                  );
                  chatSessionKey.chatKey = data.liveAgentSessionKey;

                  const result = window.sforce.connection.login(
                    LiveChatConstants.TRANSCRIPT_USERNAME,
                    LiveChatConstants.TRANSCRIPT_PASSWORD
                  );

                  console.log("sessionId = ", result.sessionId);

                  const res = window.sforce.apex.execute(
                    "SCC_ChatTranscriptHandler",
                    "emailChatTranscript",
                    { chatSesionKey: chatSessionKey }
                  );

                  console.log("chatId = ", res);
                } catch (e) {
                  console.error("Exception sending transcript:", e);
                }
              });

              if (node.parentNode) {
                node.parentNode.insertBefore(btn, node.nextSibling);
              }
            }, 100);
          };

          // Add event handlers
          const chatEndEvents = [
            "onChatEndedByChasitor",
            "onChatEndedByAgent",
            "onChatEndedByChatbot",
            "onIdleTimeoutOccurred",
          ];

          chatEndEvents.forEach((eventName) => {
            embedded_svc.addEventHandler(eventName, (data: any) => {
              console.log(`${eventName}: ${data.liveAgentSessionKey}`);
              createTranscriptButton(data);
            });
          });

          // Privacy policy handler
          let privacyFlag = true;
          embedded_svc.addEventHandler("afterMaximize", () => {
            if (privacyFlag) {
              privacyFlag = false;
              setTimeout(() => {
                const node = document.querySelector(".fieldList");
                if (node && node.parentNode) {
                  const ele = document.createElement("div");
                  ele.className = "remove";
                  ele.innerHTML =
                    `By filling in your information and proceeding you agree to our 
                    <a aria-label="Privacy Policy and Terms of Use" href="https://www.sobeys.com/privacy-policy">Privacy Policy and Terms of Use</a>`;
                  node.parentNode.insertBefore(ele, node.nextSibling);
                }
              }, 100);
            }
          });

          // Initialize the embedded service
          try {
            embedded_svc.init(
              LiveChatConstants.SVC_INIT_ONE,
              LiveChatConstants.SVC_INIT_TWO,
              gslbBaseURL,
              LiveChatConstants.ORG_ID,
              LiveChatConstants.BANNER,
              {
                baseLiveAgentContentURL: LiveChatConstants.BASE_LIVE_AGENT_CONTENT_URL,
                deploymentId: LiveChatConstants.DEPLOYMENT_ID,
                buttonId: LiveChatConstants.BUTTON_ID,
                baseLiveAgentURL: LiveChatConstants.BASE_LIVE_AGENT_URL,
                eswLiveAgentDevName: LiveChatConstants.LIVE_AGENT_DEV_NAME,
                isOfflineSupportEnabled: true,
                targetElement: "#sfsc_live_cchat",
              }
            );

            console.log("Embedded service initialized successfully");
            isInitialized = true;
          } catch (error) {
            console.error("Failed to initialize embedded service:", error);
          }
        };

        // Load the embedded service script
        if (!window.embedded_svc) {
          console.log("Loading embedded service script...");
          await loadScript(LiveChatConstants.ESW_SCRIPT);

          // Wait for the service to be available with timeout
          let attempts = 0;
          const maxAttempts = 50;

          const waitForService = async (): Promise<boolean> => {
            while (attempts < maxAttempts && !window.embedded_svc) {
              await new Promise((resolve) => setTimeout(resolve, 100));
              attempts++;
            }
            return !!window.embedded_svc;
          };

          const serviceReady = await waitForService();

          if (serviceReady) {
            // Small delay to ensure everything is ready
            setTimeout(() => initESW(null), 300);
          } else {
            console.error(
              "Embedded service failed to load within timeout period"
            );
          }
        } else {
          console.log("Embedded service already loaded");
          initESW(LiveChatConstants.ESW_INIT_SCRIPT);
        }
      } catch (error) {
        console.error("Failed to initialize Salesforce chat:", error);
      }
    };

    // Start initialization after DOM is ready
    const timer = setTimeout(() => {
      initializeChat();
    }, 1500);

    // Cleanup function
    return () => {
      clearTimeout(timer);
    };
  }, [isClient, language, provinceCode]);

  // Render target element for chat widget
  return <>{isClient && <div id="sfsc_live_cchat" />}</>;
};

export default SalesforceChat;
